import type { HTMLAttributes, ReactElement, ReactNode, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../Accordion';
import type { ButtonProps } from '../Button';
import SvgWarehouse from './icons/warehouse';

export interface NotFoundProps {
    variant?: 'default' | 'dark';
    errorCode?: number | null;
    errorMessage?: string;
    details?: ReactNode;
    cta?: ReactElement<ButtonProps>;
    IconComponent?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
}

export const NotFound = ({
    errorCode = 404,
    errorMessage,
    IconComponent = SvgWarehouse,
    cta,
    dataTestid,
    details,
    htmlAttributes,
}: NotFoundProps) => {
    const { t } = useTranslation();
    return (
        <div
            className='flex min-h-full flex-col bg-gray-100 p-6 text-black'
            data-testid={dataTestid}
            data-component-name='NotFound'
            {...htmlAttributes}
        >
            <div className='ml-8 mt-12'>
                {errorCode && <h1 className='my-4 font-campton text-8xl font-bold'>{errorCode} !</h1>}
                <p className='mb-8 font-campton text-2xl font-semibold'>
                    {errorMessage ?? `${t("For some reason, we couldn't load")} 😓`}
                </p>
                {details ? (
                    <Accordion
                        type='single'
                        collapsible
                        defaultValue={getEnvironmentVariable('mode') === 'development' ? 'item-1' : undefined}
                    >
                        <AccordionItem
                            value='item-1'
                            className='border-none'
                        >
                            <AccordionTrigger className='justify-start gap-2'>{t('See error')}</AccordionTrigger>
                            <AccordionContent>{details}</AccordionContent>
                        </AccordionItem>
                    </Accordion>
                ) : null}
                {cta && <div className='my-4'>{cta}</div>}
            </div>
            <div className='mt-auto flex justify-end'>
                <IconComponent className={'m-2 mr-8 size-[400px] text-white'} />
            </div>
        </div>
    );
};
